import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import useSsoLinkRefresh from '@/hooks/useSsoLinkRefresh';
import eventMessagesService from '@/services/events/eventMessagesService';
import {markExternalLinkOpened} from '@/services/events/externalLinkReturnTracker';
import {selectSsoLink} from '@/state/selectors/global';
import {getState, subscribe} from '@/state/store';
import {formatByName} from '@/utils/stringUtils';

/**
 * External link configuration coming either from AEM content (ssoTemplate /
 * externalLinkCode / ssoUrl) or built ad-hoc on the call site for direct URLs.
 *
 * Mirrors the legacy UWM Loyalty contract — the parent app
 * (iqosconnect-pwa-npi) reads the same fields in `uwmExternalLinkService`.
 */
export type ExternalLinkConfig = {
    externalLinkCode?: string;
    ssoTemplate?: string;
    ssoUrl?: string;
    linkType?: string;
};

export type ExternalLinkParams = Record<string, string | number | undefined>;

type Options = {
    externalLink?: ExternalLinkConfig | null;
    params?: ExternalLinkParams;
};

type BatchOptions = {
    items: Options[];
};

const DEFAULT_TIMEOUT = 10_000;

const blankLinkOpen = (url: string) => {
    if (!url) return;
    markExternalLinkOpened();
    window.open(url, '_blank');
};

const getFormattedSsoTemplate = ({
    ssoTemplate,
    params,
}: {
    ssoTemplate?: string;
    params?: ExternalLinkParams;
}): string | null => {
    if (!ssoTemplate) return null;
    return formatByName(ssoTemplate, params);
};

// Module-level batch buffer (debounced flush). Mirrors legacy implementation.
type PendingEntry = {
    externalLink: ExternalLinkConfig;
    params?: ExternalLinkParams;
    formattedSsoTemplate: string | null;
};
let pendingExternalLinks: PendingEntry[] = [];
let flushTimerId: ReturnType<typeof setTimeout> | null = null;

const flushPending = () => {
    if (!pendingExternalLinks.length) return;
    eventMessagesService.sendGetExternalLinks(pendingExternalLinks.map((x) => ({...x})));
    pendingExternalLinks = [];
};

const scheduleFlush = () => {
    if (flushTimerId) clearTimeout(flushTimerId);
    flushTimerId = setTimeout(() => {
        flushTimerId = null;
        flushPending();
    }, 500);
};

const useSsoLinkFetch = ({
    externalLink,
    params,
    status,
    formattedSsoTemplate,
}: {
    externalLink?: ExternalLinkConfig | null;
    params?: ExternalLinkParams;
    status?: string;
    formattedSsoTemplate: string | null;
}) => {
    useEffect(() => {
        if (!externalLink) return;
        if (status) return;
        if (!formattedSsoTemplate) return;
        if (pendingExternalLinks.find((x) => x.formattedSsoTemplate === formattedSsoTemplate)) return;

        pendingExternalLinks.push({externalLink, params, formattedSsoTemplate});
        scheduleFlush();
    }, [formattedSsoTemplate, status]);
};

let timeoutId: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;

const waitForExternalLink = (formattedSsoTemplate: string, timeout = DEFAULT_TIMEOUT): Promise<string> =>
    new Promise((resolve, reject) => {
        // Clear previous timeout and unsubscribe — only one wait in flight.
        if (timeoutId) clearTimeout(timeoutId);
        if (unsubscribe) unsubscribe();

        const existing = selectSsoLink(formattedSsoTemplate)(getState());
        if (existing?.link) {
            resolve(existing.link);
            return;
        }

        timeoutId = setTimeout(() => {
            unsubscribe?.();
            reject(new Error('Timeout waiting for external link'));
        }, timeout);

        unsubscribe = subscribe(() => {
            const ssoLink = selectSsoLink(formattedSsoTemplate)(getState());
            if (ssoLink?.link) {
                if (timeoutId) clearTimeout(timeoutId);
                unsubscribe?.();
                resolve(ssoLink.link);
            }
        });
    });

/**
 * Returns a click handler that opens an external link. Mirrors the legacy UWM
 * Loyalty `useExternalLinks` hook:
 *
 * 1. If a resolved SSO link exists in the store, open it directly.
 * 2. Otherwise, send `UWM_MODULE_GET_EXTERNAL_LINKS` to the parent app, wait
 *    for `UWM_INIT_EXTERNAL_LINKS` to populate the store, then open it.
 */
const useExternalLinks = ({externalLink, params}: Options): (() => void) | null => {
    const {externalLinkCode, ssoTemplate, ssoUrl} = externalLink ?? {};
    const template = externalLinkCode || ssoTemplate || ssoUrl;
    const formattedSsoTemplate = getFormattedSsoTemplate({ssoTemplate: template, params});
    const {link, status, expirationDate} = useSelector(selectSsoLink(formattedSsoTemplate));

    useSsoLinkFetch({externalLink, params, status, formattedSsoTemplate});
    useSsoLinkRefresh({expirationDate, formattedSsoTemplate});

    if (!externalLink || !formattedSsoTemplate) return null;

    if (link) {
        return () => blankLinkOpen(link);
    }

    return async () => {
        try {
            eventMessagesService.sendGetExternalLinks([{externalLink, params, formattedSsoTemplate}], true);

            const receivedLink = await waitForExternalLink(formattedSsoTemplate);

            if (receivedLink) {
                blankLinkOpen(receivedLink);
            }
        } catch (error) {
            // Match legacy behaviour: just log and let the user click again.
            // eslint-disable-next-line no-console
            console.info('Error redirecting to external link', error);
        }
    };
};

export const useExternalLinksBatch = ({items}: BatchOptions) => {
    useEffect(() => {
        if (!items.length) return;

        const pendingByTemplate = new Map<string, PendingEntry>();

        for (const {externalLink, params} of items) {
            if (!externalLink) continue;

            const {externalLinkCode, ssoTemplate, ssoUrl} = externalLink;
            const template = externalLinkCode || ssoTemplate || ssoUrl;
            if (!template) continue;

            const formattedSsoTemplate = getFormattedSsoTemplate({ssoTemplate: template, params});
            if (!formattedSsoTemplate) continue;

            const {status} = selectSsoLink(formattedSsoTemplate)(getState());
            if (status) continue;

            if (pendingExternalLinks.find((x) => x.formattedSsoTemplate === formattedSsoTemplate)) continue;

            if (!pendingByTemplate.has(formattedSsoTemplate)) {
                pendingByTemplate.set(formattedSsoTemplate, {externalLink, params, formattedSsoTemplate});
            }
        }

        if (!pendingByTemplate.size) return;

        pendingExternalLinks.push(...pendingByTemplate.values());
        scheduleFlush();
    }, [items]);
};

export default useExternalLinks;
